import {
    FieldTypes,
    IField,
    ISortedField,
    V2,
    deleteObjectById,
    duplicateObject,
    generateID,
    getJsonAltKeyFromField,
    getJsonKeyFromField,
    getObjectById,
    sortFields,
    spLogTags,
    structuredCloneL,
    tryParseJSON
} from '@eventr/common'
import { useValidatorContext } from '@eventr/common-storybook'
import { useSignal, useSignalEffect } from '@preact/signals-react'
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useParams } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'
import { ISection } from '../../data/Data'
import { bs, useBoundStore } from '../../state/bound.store'
import { defaultAddSnack } from '../../state/overlayState/overlayContext'
import { sectionInfoToEmptyV2 } from '../../utils'

interface IEditSectionContext {
    sectionData?: V2
    section?: ISection
    updateValues: (value: IUpdateValue) => void
    deleteSetValue?: (value: IUpdateValue) => void
    createNewItem?: (id: string, listId?: string, field?: IField) => void
    getValue: (
        field: IField,
        _id?: string,
        prettyPrint?: boolean,
        altValue?: boolean
    ) => any
    values?: any
    isListTypeSection?: boolean
    onCancelEdit?: () => void
    onSaveData?: (id: string) => Promise<void>
    anyPendingChanges?: boolean
    isNewItem: (id: string) => boolean
    getItinTitle?: () => string | undefined
    setNewItinMode?: (value: boolean) => void
    alreadyPopulated?: boolean
    nowSetAlreadyPopulated?: (value: boolean) => void
    deleteItem?: (_id: string) => void
    duplicateItem?: (_id: string) => void
}

interface IUpdateValue {
    value: any
    field?: IField
    _id?: string
    valueAlt?: any
}

const prefillRules: Record<number, string> = {
    228: 'array',
    229: 'itinMembers',
    311: 'array',
    312: 'itinMembers',
    314: 'itinStart',
    315: 'itinEnd',
    417: 'array',
    418: 'itinMembers',
    407: 'itinStart',
    409: 'itinEnd',
    515: 'array',
    516: 'itinMembers',
    502: 'itinStart',
    504: 'itinEnd',
    808: 'array',
    809: 'itinMembers',
    802: 'itinStart',
    903: 'array',
    905: 'itinStart',
    906: 'itinEnd',
    911: 'itinMembers',
}

const EditSectionContext = createContext<IEditSectionContext>({
    sectionData: undefined,
    section: undefined,
    updateValues: () => { },
    deleteSetValue: undefined,
    createNewItem: undefined,
    getValue: () => null,
    values: undefined,
    isListTypeSection: undefined,
    onCancelEdit: undefined,
    isNewItem: () => false,
})

export const EditSectionProvider = (props: { children: React.ReactNode }) => {
    const {
        getEventInfoV2,
        updateItinerarySection,
        getFieldsForSection,
        sections,
    } = useBoundStore(
        useShallow((s) => {
            return {
                getEventInfoV2: s.getEventInfoV2,
                updateItinerarySection: s.updateItinerarySection,
                sections: s.sections,
                getFieldsForSection: s.getFieldsForSection,
            }
        })
    )

    const [section, setSection] = useState<ISection>()
    const [values, setValues] = useState<any>({})
    const [sectionData, setSectionData] = useState<V2>()
    const [sortedFields, setSortedFields] = useState<ISortedField[]>([])
    const [alreadyPopulated, setAlreadyPopulated] = useState<boolean>(false)
    const { isAllValid, setShowErrors, setValidationOn } = useValidatorContext()
    const ogValues = useSignal({})
    const { sectionID, isNew } = useParams<{
        sectionID: string
        isNew: string
    }>()

    //set Initial values for section

    useSignalEffect(() => {
        initLoad()
    })

    useEffect(() => {
        const section = sections?.find((section) => {
            if (sectionID) {
                return section.id === Number.parseInt(sectionID)
            }
            return false
        })

        setSection(section)
    }, [sectionID, sections])

    useEffect(() => {
        if (sectionID === undefined) return
        const sortedFields = sortFields(
            getFieldsForSection(Number.parseInt(sectionID)),
            false
        )
        setSortedFields(sortedFields)
    }, [sectionID])

    const initLoad = () => {
        if (sectionID === undefined) return
        const sectionV2Data =
            bs().getSectionV2FromSectionIdFromSelectedItinerary(
                Number.parseInt(sectionID)
            )

        if (sectionV2Data === undefined) {
            const sectionInfo = bs().getSection(Number.parseInt(sectionID))
            if (sectionInfo === undefined) throw new Error('Section not found')
            const v2 = structuredCloneL(sectionInfoToEmptyV2(sectionInfo))
            setSectionData(v2)
        } else {
            setSectionData(structuredCloneL(sectionV2Data))
        }

        if (isNew === 'new') {
            setValues({})
            ogValues.value = {}
        } else {
            try {
                setValues(JSON.parse(JSON.stringify(sectionV2Data?.data)))
                ogValues.value = JSON.parse(JSON.stringify(sectionV2Data?.data))
            } catch {
                /// Cant parse...
            }
        }
    }

    const updateValues = useCallback(
        (value: IUpdateValue) => {
            if (value._id) {
                let obj = getObjectById(value._id, values)
                if (obj) {
                    if (value.field) {
                        obj[getJsonKeyFromField(value.field)] = value.value
                        if (getJsonAltKeyFromField(value.field)) {
                            obj[getJsonAltKeyFromField(value.field)] =
                                value.valueAlt
                        }

                        if (value._id === '0') {
                            setValues({ ...obj })
                        } else {
                            setValues({ ...values })
                        }
                        return
                    } else {
                        obj = value.value

                        if (value._id === '0') {
                            setValues({ ...obj })
                        } else {
                            setValues({ ...values })
                        }
                        return
                    }
                }
            }
        },
        [values]
    )

    const deleteSetValue = useCallback(
        (value: IUpdateValue) => {
            if (value._id) {
                let obj = getObjectById(value._id, values)
                if (obj) {
                    if (value.field) {
                        delete obj[getJsonKeyFromField(value.field)]

                        if (value._id === '0') {
                            setValues({ ...obj })
                        } else {
                            setValues({ ...values })
                        }
                        return
                    } else {
                        return
                    }
                }
            }
        },
        [values]
    )

    const populateItemWithInitialValues = (fields: ISortedField[]) => {
        const obj: any = {}
        const selectedItinID = bs().selectedItinID
        for (const field of fields) {
            if (prefillRules[field.field.id]) {
                const rule = prefillRules[field.field.id]
                switch (rule) {
                    case 'array':
                        obj[getJsonKeyFromField(field.field)] = []
                        break
                    case 'itinMembers':
                        obj[getJsonKeyFromField(field.field)] = useBoundStore
                            .getState()
                            .selectedItineraryUsers()
                            .map((u) => ({
                                type: 'User',
                                option: u.id,
                                _id: section ? generateID(selectedItinID, section.id) : undefined,
                            }))
                        break
                    case 'itinStart':
                        obj[getJsonKeyFromField(field.field)] =
                            getEventInfoV2()?.data?.start_date

                        break
                    case 'itinEnd':
                        obj[getJsonKeyFromField(field.field)] =
                            getEventInfoV2()?.data?.end_date

                        break
                }
                if (field.children) {
                    if (field.field.field_type === FieldTypes.list) {
                        obj[getJsonKeyFromField(field.field)].push({
                            _id: section?.id !== undefined ? generateID(selectedItinID, section.id) : undefined,
                            ...populateItemWithInitialValues(field.children),
                        })
                    }
                }
            }
        }
        return obj
    }

    const createNewItem = useCallback(
        (_id: string, idOfList?: string, field?: IField) => {
            if (!idOfList) {
                if (!values[getJsonKeyFromField(sortedFields[0].field)]) {
                    values[getJsonKeyFromField(sortedFields[0].field)] = []
                }
                const pop = populateItemWithInitialValues(
                    sortedFields[0].children
                )
                values[getJsonKeyFromField(sortedFields[0].field)].push({
                    _id: _id,
                    ...pop,
                })
                setValues({ ...values })
            } else {
                let obj = getObjectById(idOfList, values)

                if (obj && field) {

                    if (!obj[getJsonKeyFromField(field)]) {
                        obj[getJsonKeyFromField(field)] = []
                    }
                    obj[getJsonKeyFromField(field)].push({
                        _id: _id,
                    })
                    setValues({ ...values })
                }
            }
        },
        [sortedFields, values]
    )

    const deleteItem = useCallback(
        (_id: string | string[]) => {
            let newValues = values

            if (Array.isArray(_id)) {
                _id.forEach((id) => {
                    newValues = deleteObjectById(id, newValues)
                })
            } else {
                newValues = deleteObjectById(_id, values)
            }

            setValues({ ...newValues })
            setSectionData((v) => {
                if (v && typeof v === 'object' && 'data' in v) {
                    return { ...v, data: newValues }
                }
                return v // Return the original value if the type check fails
            })
        },
        [values]
    )
    const duplicateItem = useCallback(
        (_id: string) => {
            const updatedValue = duplicateObject(_id, values)
            setValues({ ...updatedValue })
            setSectionData((v) => {
                if (v && typeof v === 'object' && 'data' in v) {
                    return { ...v, data: updatedValue }
                }

            })
        },
        [values]
    )

    const prettyPrintValue = useCallback((field: IField, value: any) => {
        if (
            field.field_type === FieldTypes.text &&
            field.type_properties?.data_source === 'users'
        ) {
            const parsed = tryParseJSON(value)
            const user = bs().getUserFromID(parsed.option)
            if (user) {
                return user.firstName + ' ' + user.lastName
            } else {
                return ''
            }
        }
        if (
            field.field_type === FieldTypes.select &&
            field.type_properties?.data_source === 'usersWithTags'
        ) {
            const parsed = tryParseJSON(value)
            return parsed.map((u: any) => {
                if (u.type === 'User') {
                    const user = useBoundStore
                        .getState()
                        .getUserFromID(u.option)
                    return user ? bs().getNameFormatted(user) : ''
                } else {
                    return u.option
                }
            })
        }
        return value
    }, [])

    const getValue = useCallback(
        (
            field: IField,
            _id?: string,
            prettyPrint?: boolean,
            altValue: boolean = false
        ) => {
            if (section === undefined || !field) {
                return ''
            }

            let key = getJsonKeyFromField(field)

            if (altValue) {
                key = getJsonAltKeyFromField(field)
            }

            if (field.parent === null) {
                if (values && values.hasOwnProperty(key)) return values[key]
                if (field.field_type === FieldTypes.list) {
                    return []
                }
                if (field.field_type === FieldTypes.group) {
                    return {}
                }

                return null
            }
            if (_id) {
                const obj = getObjectById(_id, values)
                if (obj?.hasOwnProperty(key)) {
                    if (prettyPrint) {
                        return prettyPrintValue(field, obj[key])
                    }
                    return obj[key]
                } else {
                    if (field.field_type === FieldTypes.list) {
                        return []
                    }
                    if (field.field_type === FieldTypes.group) {
                        return {}
                    }
                }

                return ''
            }

            return ''
        },
        [values, section, prettyPrintValue]
    )

    const isListTypeSection = useMemo(() => {
        if (sortedFields === undefined || sortedFields.length === 0)
            return false
        return sortedFields[0].field.field_type === FieldTypes.list
    }, [sortedFields])

    const onCancelEdit = () => {
        if (!sectionData) return
        const hardCopy = JSON.parse(JSON.stringify(ogValues.value))
        setValues(hardCopy)
        sectionData.data = hardCopy
        const copySectionData = structuredCloneL(sectionData)
        setSectionData(copySectionData)
    }

    const onSaveData = async (_id: string) => {
        let saveData
        if (!isAllValid) {
            spLogTags('Fields not valid for saving', ['SAVE'])
            setShowErrors(true)
            return
        }
        setValidationOn(false)
        if (_id === '0') {
            saveData = values
        } else {
            saveData = getObjectById(_id, values)
        }

        if (section && await updateItinerarySection(section.id, saveData)) {
            nowSetAlreadyPopulated(false)
            initLoad()
            window.goBack()
        } else {
            defaultAddSnack('Did not add/update correctly', 'error')
        }
    }

    const anyPendingChanges = useMemo(() => {
        if (JSON.stringify(values) === JSON.stringify(ogValues.value)) {
            return false
        }

        return true
    }, [values, ogValues])

    const isNewItem = useCallback(
        (_id: string) => {
            if (_id === '0') {
                return false
            }
            const ogVal = getObjectById(_id, ogValues.value)
            if (ogVal) {
                return false
            }
            return true
        },
        [ogValues]
    )

    const nowSetAlreadyPopulated = (ans: boolean) => {
        setAlreadyPopulated(ans)
    }

    const getItinTitle = () => {
        const eventInfo = getEventInfoV2()
        if (eventInfo) {
            return eventInfo.data.title
        }
    }

    return (
        <EditSectionContext.Provider
            value={{
                sectionData,
                section,
                updateValues,
                deleteSetValue,
                createNewItem,
                getValue,
                values,
                isListTypeSection,
                onCancelEdit,
                onSaveData,
                anyPendingChanges,
                isNewItem,
                getItinTitle,
                nowSetAlreadyPopulated,
                alreadyPopulated,
                deleteItem,
                duplicateItem,
            }}
        >
            {sectionData && props.children}
        </EditSectionContext.Provider>
    )
}

export const useEditSectionContext = () => {
    return useContext(EditSectionContext)
}
